import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, query, updateDoc, where, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.jsx';

// Escalation chain configuration per team:
//   - L1 = team.responderUids (configured in Teams.jsx) + team.responseWindowSeconds (this page)
//   - L2+ = team.additionalLevels[] — each level has its own responderUids[] and windowSeconds
// When a ticket isn't accepted in a level's window, the engine escalates to the next level.

const DEFAULT_L1_WINDOW = 300;   // 5 min
const DEFAULT_LX_WINDOW = 600;   // 10 min for L2; admins can change

export default function EscalationChains() {
  const { claims } = useAuth();
  const vendorId = claims?.vendor_id;

  const [teams, setTeams] = useState(null);
  const [responders, setResponders] = useState([]);
  const [error, setError] = useState(null);
  const [savingTeamId, setSavingTeamId] = useState(null);

  useEffect(() => {
    if (!vendorId) return;
    const unsubTeams = onSnapshot(
      collection(db, 'vendors', vendorId, 'teams'),
      (snap) => setTeams(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => setError(err.message),
    );
    const unsubResp = onSnapshot(
      query(collection(db, 'vendors', vendorId, 'users'), where('role', 'in', ['responder', 'vendor_admin'])),
      (snap) => setResponders(
        snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((u) => !u.deletedAt),
      ),
      (err) => setError(err.message),
    );
    return () => { unsubTeams(); unsubResp(); };
  }, [vendorId]);

  async function persist(team, patch) {
    setSavingTeamId(team.id); setError(null);
    try {
      await updateDoc(doc(db, 'vendors', vendorId, 'teams', team.id), {
        ...patch,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingTeamId(null);
    }
  }

  return (
    <>
      <h2>Escalation Chains</h2>
      <p style={{ color: 'var(--muted)', marginTop: -8 }}>
        For each team, set the L1 response window and add L2+ levels. The engine rings the L1 list,
        and if no one accepts within the window, escalates to L2, then L3, and so on.
      </p>

      {error && <div className="error">{error}</div>}
      {teams === null && <div className="placeholder">Loading…</div>}
      {teams && teams.length === 0 && (
        <div className="placeholder">Create a team first under <strong>Teams</strong>.</div>
      )}
      {teams && teams.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {teams.map((team) => (
            <ChainEditor
              key={team.id}
              team={team}
              responders={responders}
              saving={savingTeamId === team.id}
              persist={(patch) => persist(team, patch)}
            />
          ))}
        </div>
      )}
    </>
  );
}

function ChainEditor({ team, responders, saving, persist }) {
  const l1Window = team.responseWindowSeconds ?? DEFAULT_L1_WINDOW;
  const additional = team.additionalLevels || [];
  const l1Responders = (team.responderUids || []).map((uid) => responders.find((r) => r.id === uid)).filter(Boolean);

  function setL1Window(seconds) {
    persist({ responseWindowSeconds: seconds });
  }

  function addLevel() {
    persist({ additionalLevels: [...additional, { responderUids: [], windowSeconds: DEFAULT_LX_WINDOW }] });
  }

  function removeLevel(index) {
    if (!confirm(`Remove L${index + 2}? Tickets currently escalating will skip it.`)) return;
    const next = additional.filter((_, i) => i !== index);
    persist({ additionalLevels: next });
  }

  function updateLevel(index, patch) {
    const next = additional.map((lvl, i) => (i === index ? { ...lvl, ...patch } : lvl));
    persist({ additionalLevels: next });
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3>{team.name}</h3>
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>{saving ? 'Saving…' : ''}</span>
      </div>

      <LevelRow
        label="L1 (primary)"
        members={l1Responders}
        windowSeconds={l1Window}
        onWindowChange={setL1Window}
        editMembershipHint="Edit the L1 list under Teams."
      />

      {additional.map((lvl, i) => (
        <LevelRow
          key={i}
          label={`L${i + 2}`}
          members={(lvl.responderUids || []).map((uid) => responders.find((r) => r.id === uid)).filter(Boolean)}
          unassigned={responders.filter((r) => !(lvl.responderUids || []).includes(r.id))}
          windowSeconds={lvl.windowSeconds}
          onWindowChange={(s) => updateLevel(i, { windowSeconds: s })}
          onAddMember={(uid) => updateLevel(i, { responderUids: [...(lvl.responderUids || []), uid] })}
          onRemoveMember={(uid) => updateLevel(i, { responderUids: (lvl.responderUids || []).filter((u) => u !== uid) })}
          onRemoveLevel={() => removeLevel(i)}
        />
      ))}

      <div style={{ marginTop: 16 }}>
        <button onClick={addLevel} className="btn-ghost">+ Add escalation level (L{additional.length + 2})</button>
      </div>
    </div>
  );
}

function LevelRow({
  label, members, unassigned = [], windowSeconds,
  onWindowChange, onAddMember, onRemoveMember, onRemoveLevel,
  editMembershipHint,
}) {
  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <strong style={{ minWidth: 90 }}>{label}</strong>
        <WindowInput seconds={windowSeconds} onChange={onWindowChange} />
        {onRemoveLevel && (
          <button onClick={onRemoveLevel} className="btn-ghost-danger" style={{ marginLeft: 'auto' }}>
            Remove level
          </button>
        )}
      </div>

      <div style={{ marginTop: 10 }}>
        {members.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>
            No responders.{editMembershipHint && ' ' + editMembershipHint}
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {members.map((m) => (
              <span key={m.id} className="btn-chip-active">
                {m.fullName}
                {onRemoveMember && (
                  <button
                    onClick={() => onRemoveMember(m.id)}
                    style={{ background: 'transparent', border: 'none', color: 'inherit', marginLeft: 6, cursor: 'pointer' }}
                  >×</button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>

      {onAddMember && unassigned.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <small style={{ color: 'var(--muted)' }}>Add: </small>
          {unassigned.map((r) => (
            <button key={r.id} onClick={() => onAddMember(r.id)} className="btn-chip" style={{ marginRight: 4 }}>
              + {r.fullName}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function WindowInput({ seconds, onChange }) {
  const [local, setLocal] = useState(secondsToMinutes(seconds));
  useEffect(() => { setLocal(secondsToMinutes(seconds)); }, [seconds]);

  function commit() {
    const m = parseFloat(local);
    if (isNaN(m) || m < 0.5) return;
    const newSeconds = Math.round(m * 60);
    if (newSeconds !== seconds) onChange(newSeconds);
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 13, color: 'var(--muted)' }}>Window:</span>
      <input
        type="number" min="0.5" step="0.5"
        value={local} onChange={(e) => setLocal(e.target.value)}
        onBlur={commit} onKeyDown={(e) => e.key === 'Enter' && commit()}
        style={{ width: 70, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}
      />
      <span style={{ fontSize: 13, color: 'var(--muted)' }}>min</span>
    </span>
  );
}

function secondsToMinutes(s) {
  return Math.round((s / 60) * 10) / 10;
}
